import React from 'react';
import { Link } from 'react-router-dom';
import MomLayout from './MomLayout';
import { useMomAuth } from '../../context/MomAuthContext';
import { getWeekInfo } from '../../lib/pregnancy-content';
import { Sparkles, Camera, Video, User, LogOut, Wand2 } from 'lucide-react';
import { CreditPanel } from '../../components/credits/CreditPanel';

export default function MomDashboardPage() {
  const { mom, logout } = useMomAuth();

  const week = mom?.pregnancy_week || 0;
  const info = getWeekInfo(week) || { size: '—', description: 'Bilgi yok', color: '#FB7185' };
  const progress = Math.min(100, Math.round((week / 40) * 100));

  return (
    <MomLayout>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm text-[#7A6C68]">Merhaba,</p>
          <h1 className="text-2xl font-bold text-[#2D2422]" data-testid="mom-dashboard-greeting">
            {mom?.first_name || 'Anne'} 🤍
          </h1>
        </div>
        <button onClick={logout} className="w-10 h-10 rounded-2xl bg-white border border-[#F3E5E0] flex items-center justify-center text-[#7A6C68] hover:text-[#FB7185]"
          data-testid="mom-dashboard-logout-btn">
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {/* Week hero card */}
      <div className="rounded-3xl p-6 mb-5 shadow-sm relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${info.color}, #FBBF24)` }}
        data-testid="mom-dashboard-week-card">
        <div className="relative z-10">
          <p className="text-white/80 text-sm font-medium mb-1">Gebelik</p>
          <div className="flex items-baseline gap-2">
            <p className="text-6xl font-bold text-white leading-none" data-testid="mom-dashboard-week">{week || '—'}</p>
            <p className="text-white/90 text-lg">. hafta</p>
          </div>
          <p className="text-white/90 text-sm mt-4 mb-2 font-medium">
            Bu hafta bebeğin boyutu: <span className="font-semibold">{info.size}</span>
          </p>
          <p className="text-white/85 text-sm leading-relaxed">{info.description}</p>
          {/* Progress bar */}
          <div className="mt-5">
            <div className="flex justify-between text-xs text-white/80 mb-1.5">
              <span>1. hafta</span>
              <span data-testid="mom-dashboard-progress-percent">%{progress}</span>
              <span>40. hafta</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all" style={{ width: `${progress}%` }}></div>
            </div>
          </div>
        </div>
        <Sparkles className="absolute top-5 right-5 w-6 h-6 text-white/40" />
      </div>

      {/* Action cards */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <ActionCard
          icon={<Camera className="w-6 h-6" />}
          title="4K Yüz Üret"
          subtitle="Ultrason · 1 kredi"
          to="/mom/upload"
          testid="mom-dashboard-action-4k"
        />
        <ActionCard
          icon={<Video className="w-6 h-6" />}
          title="Video Animasyon"
          subtitle="4D · 2 kredi"
          to="/mom/video"
          testid="mom-dashboard-action-video"
        />
      </div>

      {/* Genetic Fusion — primary B2C feature */}
      <Link
        to="/mom/ai-face"
        className="block rounded-3xl p-5 mb-5 shadow-sm relative overflow-hidden active:scale-[0.99] transition-transform"
        style={{ background: 'linear-gradient(135deg, #FB7185, #F472B6)' }}
        data-testid="mom-dashboard-action-aiface"
      >
        <Sparkles className="absolute top-4 right-4 w-5 h-5 text-white/40" />
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center text-white">
            <Wand2 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold">Genetik Füzyon</p>
            <p className="text-white/85 text-xs mt-0.5">Anne &amp; baba fotoğrafından AI bebek yüzü · 1 kredi</p>
          </div>
        </div>
      </Link>

      {/* Profile */}
      <Link to="/mom/profile" className="rounded-3xl bg-white p-4 shadow-sm border border-[#F3E5E0] flex items-center gap-3 hover:bg-[#FFF0ED] transition-colors"
        data-testid="mom-dashboard-profile-link">
        <div className="w-10 h-10 rounded-2xl bg-[#FFE4DF] flex items-center justify-center">
          <User className="w-5 h-5 text-[#FB7185]" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-[#2D2422]">Profilim</p>
          <p className="text-xs text-[#7A6C68]">Bilgilerini güncelle</p>
        </div>
      </Link>

      {/* Step 8: Credit balance + free 4K + redeem + packages */}
      <div className="mt-5">
        <CreditPanel variant="mom" />
      </div>
    </MomLayout>
  );
}

const ActionCard = ({ icon, title, subtitle, to, testid }) => {
  const body = (
    <div className="rounded-3xl p-5 shadow-sm border transition-all bg-white border-[#F3E5E0] hover:shadow-md active:scale-95">
      <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3 bg-[#FFE4DF] text-[#FB7185]">
        {icon}
      </div>
      <p className="font-semibold text-[#2D2422] text-sm">{title}</p>
      <p className="text-xs text-[#7A6C68] mt-0.5">{subtitle}</p>
    </div>
  );
  return (
    <Link to={to} data-testid={testid}>{body}</Link>
  );
};
